import { type MediaAsset, type PropertyCard as PropertyCardData } from '@resolute/schema';
import { Card, cn, Modal } from '@resolute/ui';
import { useReducedMotion } from 'framer-motion';
import { GitCompare, Heart, MapPin, Phone, ShieldCheck } from 'lucide-react';
import { memo, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';


import { ProgressiveImage } from '../progressive-image';

import { useCompare } from '@/features/engagement/compare-context';
import { useWishlist } from '@/features/engagement/wishlist-context';
import { ContactCard } from '@/features/property/components/contact-card';
import { useFilterLabels } from '@/features/search/use-filter-labels';
import { useCarouselClock } from '@/lib/carousel-clock';
import { PRICE_ON_REQUEST } from '@/lib/price';
import { useInfiniteCarousel } from '@/lib/use-infinite-carousel';


/**
 * Save/compare buttons. Kept as a separate context-subscribing component so a
 * wishlist/compare toggle re-renders only this small overlay — not the whole
 * (memoized) card body — across every card in a grid.
 */
function CardActions({ property }: { property: PropertyCardData }) {
  const wishlist = useWishlist();
  const compare = useCompare();
  const saved = wishlist.has(property.id);
  const comparing = compare.has(property.id);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  return (
    <>
      <div className="absolute right-3 top-3 flex gap-1.5">
        <button
          type="button"
          aria-label={`Open ${property.title}'s location in Google Maps`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(propertyMapsHref(property), '_blank', 'noopener,noreferrer');
          }}
          className="flex size-9 items-center justify-center rounded bg-background/80 text-foreground backdrop-blur transition-colors duration-fast ease-out-expo hover:bg-background"
        >
          <MapPin className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={comparing ? 'Remove from compare' : 'Add to compare'}
          aria-pressed={comparing}
          onClick={(e) => {
            e.preventDefault();
            compare.toggle(property);
          }}
          className={cn(
            'flex size-9 items-center justify-center rounded bg-background/80 backdrop-blur transition-colors duration-fast ease-out-expo hover:bg-background',
            comparing ? 'text-primary' : 'text-foreground',
          )}
        >
          <GitCompare className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Enquire about this property"
          onClick={(e) => {
            e.preventDefault();
            setInquiryOpen(true);
          }}
          className="flex size-9 items-center justify-center rounded bg-background/80 text-foreground backdrop-blur transition-colors duration-fast ease-out-expo hover:bg-background"
        >
          <Phone className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
          aria-pressed={saved}
          onClick={(e) => {
            e.preventDefault();
            wishlist.toggle(property);
          }}
          className="flex size-9 items-center justify-center rounded bg-background/80 text-foreground backdrop-blur transition-colors duration-fast ease-out-expo hover:bg-background"
        >
          <Heart className={cn('size-4', saved && 'fill-danger text-danger')} aria-hidden="true" />
        </button>
      </div>

      <Modal
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        title={`Enquire about ${property.title}`}
      >
        <ContactCard slug={property.slug} title={property.title} bare />
      </Modal>
    </>
  );
}

/**
 * Auto-advancing image carousel for the card cover. The gallery slides
 * continuously on a loop and pauses on the current image while hovered. Falls
 * back to a single static image (no autoplay) for one-image properties or
 * reduced-motion users. Kept as its own component so its timer-driven state
 * changes stay isolated from the memoized card body.
 *
 * Advances are driven by the shared `useCarouselClock` tick (not this hook's
 * own internal timer — `auto: false` below) so every card's carousel in a
 * grid steps forward at the same moment, instead of drifting apart as each
 * card's independent timer runs on its own schedule.
 */
function CardImageCarousel({ images, title }: { images: MediaAsset[]; title: string }) {
  const reduce = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const count = images.length;
  const { slides, pos, active, animate, go, onTransitionEnd } = useInfiniteCarousel(images, {
    auto: false,
  });
  const tick = useCarouselClock(!reduce && count > 1);
  const lastTick = useRef(tick);

  useEffect(() => {
    if (tick === lastTick.current) return;
    lastTick.current = tick;
    if (!paused) go(1);
  }, [tick, paused, go]);

  if (count === 0) return <div className="aspect-[4/3] bg-muted" aria-hidden="true" />;
  if (count === 1 || reduce) {
    const img = images[0]!;
    return (
      <ProgressiveImage
        src={img.url}
        alt={img.alt || title}
        blurDataUrl={img.blurDataUrl}
        sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
      />
    );
  }

  return (
    <div
      className="relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className={cn(
          'flex will-change-transform',
          animate ? 'transition-transform duration-700 ease-out-expo' : 'transition-none',
        )}
        style={{ transform: `translateX(-${pos * 100}%)` }}
        onTransitionEnd={onTransitionEnd}
      >
        {slides.map((img, i) => (
          <div key={`${img.url}-${i}`} className="w-full shrink-0">
            {/* Every slide must load eagerly (the carousel reveals slides via
                CSS transform, not scroll, so native `loading="lazy"` never gets
                a chance to re-evaluate off-screen slides — they can end up
                permanently stuck unloaded). But only the CURRENTLY VISIBLE
                slide needs high fetch priority; forcing every slide of every
                card in the grid to high-priority piles up dozens of
                simultaneous high-priority requests, which is what was causing
                images to intermittently stall/fail to load. */}
            {/* `useInfiniteCarousel` prepends a clone of the last item and
                appends a clone of the first (indices 0 and slides.length - 1)
                so the loop wraps seamlessly — those two are decorative
                duplicates and get an empty alt; every real slide in between
                gets its own caption (falling back to the property title),
                matching the single-image case below. */}
            <ProgressiveImage
              src={img.url}
              alt={i === 0 || i === slides.length - 1 ? '' : img.alt || title}
              blurDataUrl={img.blurDataUrl}
              sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
              priority={i === pos}
              eager={i !== pos}
            />
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-2.5 flex justify-center gap-1.5">
        {images.map((img, i) => (
          <span
            key={`dot-${img.url}-${i}`}
            className={cn(
              // Fixed width, animated via `scale-x` rather than `w-*` — a
              // transform is GPU-composited, whereas animating `width`
              // triggers layout on every tick of the shared carousel clock
              // across every visible card at once.
              'h-1.5 w-4 origin-center rounded-full bg-white/55 shadow-sm transition-[transform,background-color] duration-base ease-out-expo',
              i === active ? 'scale-x-100 bg-white' : 'scale-x-[0.375]',
            )}
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Property result card. Memoised and free of context subscriptions, so it only
 * re-renders when its `property` changes — engagement toggles are isolated to
 * {@link CardActions}.
 */
export const PropertyCard = memo(function PropertyCard({
  property,
}: {
  property: PropertyCardData;
}) {
  const labels = useFilterLabels();
  // The config/area footer that used to close the card ("1 – 2 BHK Villa ·
  // 6,541 – 65,412 sq.ft") is gone, so the ranges it derived are no longer
  // computed here. Both are still shown in full on the property detail page's
  // Key facts and unit-types table.
  //
  // Prices are not published — see PRICE_ON_REQUEST. There is no "Starting
  // from" variant either: with no figure shown, a range prefix would describe
  // nothing.
  const priceLabel = PRICE_ON_REQUEST;
  const cardImages = property.images?.length
    ? property.images
    : property.coverImage
      ? [property.coverImage]
      : [];

  return (
    <Card interactive className="group overflow-hidden">
      <Link
        to={`/property/${property.slug}`}
        className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="relative">
          <CardImageCarousel images={cardImages} title={property.title} />
          <CardActions property={property} />
        </div>

        <div className="flex flex-col gap-2 p-4">
          <h3 className="line-clamp-1 text-lg font-semibold text-foreground">{property.title}</h3>

          <p className="flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="line-clamp-1">
              {property.location.locality}, {labels.city(property.location.city)}
            </span>
          </p>

          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="text-lg font-semibold tracking-tight">{priceLabel}</span>
            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-xs font-medium">
              {property.verified && (
                <span className="inline-flex items-center gap-1 text-success">
                  <ShieldCheck className="size-3.5" aria-hidden="true" /> Verified
                </span>
              )}
            </div>
          </div>

        </div>
      </Link>
    </Card>
  );
});

function formatCity(slug: string): string {
  return slug
    .split('-')
    .map((s) => (s === 'gift' ? 'GIFT' : s.charAt(0).toUpperCase() + s.slice(1)))
    .join(' ');
}

/** Google Maps "search here" deep link (no API key required) — coordinates
 *  when available (exact pin), otherwise a locality+city text query that
 *  Google geocodes itself. */
function propertyMapsHref(property: PropertyCardData): string {
  const coords = property.location.point?.coordinates;
  const query = coords
    ? `${coords[1]},${coords[0]}`
    : `${property.location.locality}, ${formatCity(property.location.city)}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
